import React from "react";
import { Settings, Target, Boxes, Users } from "lucide-react";
import img15 from "../assets/images/15.jpg";
import global from "../assets/images/global.png";
import customized from "../assets/images/customized.png";
import expert from "../assets/images/expert.png";

const AboutUs = () => {
  return (
    <section className="w-full py-20 bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Full Width Section Header */}
      <div className="w-full text-center mb-16 px-4 sm:px-6 lg:px-8">
        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 relative inline-block">
          <span className="text-orange-500 absolute -left-12 top-1/2 transform -translate-y-1/2 text-3xl">««</span>
          About Oasis Tech Consultancy LLC SPC
          <span className="text-orange-500 absolute -right-12 top-1/2 transform -translate-y-1/2 text-3xl">»»</span>
        </h2>
        <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-orange-600 mx-auto mt-6 rounded-full"></div>
      </div>

      {/* Full Width Main Content */}
      <div className="w-full">
        {/* About Us Full Width Paragraph */}
        <div className="w-full bg-white shadow-xl py-12 mb-12 border-y border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center mb-8">
              <div className="w-1 h-8 bg-orange-500 rounded-full mr-4"></div>
              <h3 className="font-bold text-2xl text-gray-900">About Us</h3>
            </div>

            <div className="max-w-6xl mx-auto space-y-6 text-gray-700">
              <p className="text-lg leading-8 text-center">
                <span className="font-semibold text-gray-900">Oasis Tech Consultancy LLC SPC</span> is the prestigious sister company of <span className="font-semibold">Zhonglian Zhihang (Beijing) Technology</span>, bringing over 10 years of proven industry expertise in delivering cutting-edge digital and AI-enabled smart solutions tailored for EPC companies in the Oil & Gas sector. Built on a strong foundation of technological innovation and deep domain knowledge, we empower clients with intelligent, efficient, and scalable project management solutions.
              </p>

              <p className="text-lg leading-8 text-center">
                Established in 2023 with its head office in Abu Dhabi, UAE, Oasis Tech has rapidly grown into a trusted partner for clients across the region. Within just two years, we have successfully executed multiple projects across Abu Dhabi, Saudi Arabia, and Iraq, earning a reputation for excellence, reliability, and innovation.
              </p>

              <p className="text-lg leading-8 text-center">
                Our parent organization, <span className="font-semibold">Zhonglian Zhihang (Beijing) Technology</span>, was founded in 2016 with a mission to deliver advanced software products for large-scale Oil & Gas construction and ground engineering projects. Our solutions help customers enhance project management efficiency, streamline construction workflows, and significantly reduce operational costs.
              </p>

              <p className="text-lg leading-8 text-center">
                Zhonglian Zhihang is a <span className="font-semibold">CMMI Level 3</span> certified company headquartered in Beijing, with additional offices in Jinan and the UAE. To date, we have successfully executed projects valued at over <span className="font-semibold">multi million USD</span> in the UAE market.
              </p>
            </div>
          </div>
        </div>

        {/* Productivity Section - Full Width */}
        <div className="w-full bg-gradient-to-r from-gray-50 to-gray-100 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Highlights Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 group text-center">
                <div className="bg-orange-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-orange-500 transition-colors duration-300 mx-auto">
                  <img src={img15} alt="Experience" className="w-8 h-8" />
                </div>
                <h4 className="font-bold text-gray-900 text-xl mb-4">10+ Years of Excellence</h4>
                <p className="text-gray-600 leading-relaxed">
                  Trusted industry expertise with proven track record across global energy projects
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 group text-center">
                <div className="bg-blue-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-500 transition-colors duration-300 mx-auto">
                  <img src={global} alt="Global" className="w-8 h-8" />
                </div>
                <h4 className="font-bold text-gray-900 text-xl mb-4">Global Presence</h4>
                <p className="text-gray-600 leading-relaxed">
                  Delivering premium services to international clients across continents
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 group text-center">
                <div className="bg-green-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-green-500 transition-colors duration-300 mx-auto">
                  <img src={expert} alt="Expert Team" className="w-8 h-8" />
                </div>
                <h4 className="font-bold text-gray-900 text-xl mb-4">Expert Team</h4>
                <p className="text-gray-600 leading-relaxed">
                  Highly skilled technical professionals with specialized industry knowledge
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 group text-center">
                <div className="bg-purple-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-purple-500 transition-colors duration-300 mx-auto">
                  <img src={customized} alt="AI Solutions" className="w-8 h-8" />
                </div>
                <h4 className="font-bold text-gray-900 text-xl mb-4">AI-Driven Innovation</h4>
                <p className="text-gray-600 leading-relaxed">
                  Cutting-edge digital transformation powered by advanced artificial intelligence
                </p>
              </div>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="bg-white p-8 border-2 border-gray-100 rounded-2xl hover:border-orange-300 hover:shadow-2xl transition-all duration-300 group">
                <div className="bg-orange-100 w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:bg-orange-500 transition-colors duration-300 mx-auto">
                  <Settings className="w-7 h-7 text-orange-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <h4 className="font-bold text-gray-900 text-xl mb-4 text-center">
                  Boost Productivity
                </h4>
                <p className="text-gray-600 text-center leading-relaxed mb-4">
                  Streamline workflows and eliminate manual operations with intelligent automation
                </p>
                <p className="text-orange-600 font-semibold text-center text-sm">
                  Save up to 40% of project time
                </p>
              </div>

              <div className="bg-white p-8 border-2 border-gray-100 rounded-2xl hover:border-orange-300 hover:shadow-2xl transition-all duration-300 group">
                <div className="bg-orange-100 w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:bg-orange-500 transition-colors duration-300 mx-auto">
                  <Target className="w-7 h-7 text-orange-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <h4 className="font-bold text-gray-900 text-xl mb-4 text-center">
                  Enhance Precision
                </h4>
                <p className="text-gray-600 text-center leading-relaxed mb-4">
                  Advanced algorithms and validation tools ensuring absolute accuracy
                </p>
                <p className="text-orange-600 font-semibold text-center text-sm">
                  Guaranteed compliance and precision
                </p>
              </div>

              <div className="bg-white p-8 border-2 border-gray-100 rounded-2xl hover:border-orange-300 hover:shadow-2xl transition-all duration-300 group">
                <div className="bg-orange-100 w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:bg-orange-500 transition-colors duration-300 mx-auto">
                  <Boxes className="w-7 h-7 text-orange-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <h4 className="font-bold text-gray-900 text-xl mb-4 text-center">
                  Seamless Integration
                </h4>
                <p className="text-gray-600 text-center leading-relaxed mb-4">
                  Fully compatible with your existing tools and enterprise systems
                </p>
                <p className="text-orange-600 font-semibold text-center text-sm">
                  Smooth transition and workflow continuity
                </p>
              </div>

              <div className="bg-white p-8 border-2 border-gray-100 rounded-2xl hover:border-orange-300 hover:shadow-2xl transition-all duration-300 group">
                <div className="bg-orange-100 w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:bg-orange-500 transition-colors duration-300 mx-auto">
                  <Users className="w-7 h-7 text-orange-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <h4 className="font-bold text-gray-900 text-xl mb-4 text-center">
                  Expert Support
                </h4>
                <p className="text-gray-600 text-center leading-relaxed mb-4">
                  Comprehensive technical support and professional training programs
                </p>
                <p className="text-orange-600 font-semibold text-center text-sm">
                  Maximize software benefits and ROI
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Vision & Mission Section - Full Width */}
        <div className="w-full bg-white py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-8 border border-orange-200 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="flex items-center mb-6">
                  <div className="w-2 h-8 bg-orange-500 rounded-full mr-4"></div>
                  <h4 className="font-bold text-gray-900 text-2xl">Our Vision</h4>
                </div>
                <p className="text-gray-700 leading-relaxed text-lg">
                  To establish ourselves as global pioneers in delivering intelligent, AI-driven, 
                  and sustainable digital solutions that revolutionize the energy, oil & gas, and 
                  engineering sectors. We envision a future where innovation, intelligence, and 
                  reliability converge to empower industries worldwide, enabling them to operate 
                  with unprecedented efficiency, safety, and environmental sustainability.
                </p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 border border-blue-200 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="flex items-center mb-6">
                  <div className="w-2 h-8 bg-blue-500 rounded-full mr-4"></div>
                  <h4 className="font-bold text-gray-900 text-2xl">Our Mission</h4>
                </div>
                <p className="text-gray-700 leading-relaxed text-lg">
                  To accelerate digital transformation across the global energy and engineering 
                  landscape by delivering intelligent, localized technology solutions that 
                  seamlessly bridge innovation and execution. We are committed to empowering 
                  EPC companies and industrial enterprises with advanced AI systems, expert 
                  technical services, and seamless integration that drives operational excellence 
                  and sustainable growth.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutUs;